import { handleAPIResponse } from '@/utils/apiUtils';
import type { Organisation } from '@/types/organisation';
import type { Job } from '@/types/jobs';
import type { Candidate, TimelineItem } from '@/types/search';
import type { PublicCandidateApiResponse } from '@/types/public';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

interface PublicOrganisationResponse {
  data: {
    organisation: Organisation;
  };
}

interface PublicJobsResponse {
  data: {
    jobs: Job[];
    pagination: {
      currentPage: number;
      totalPages: number;
      total: number;
      limit: number;
    };
  };
}

interface PublicJobResponse {
  data: {
    job: Job;
  };
}

interface PublicCandidateResponse {
  data: {
    candidate: Candidate;
    timeline?: TimelineItem[];
    timelineCount?: number;
    notesCount?: number;
    aiInterviewScheduledAt?: string;
    hasCompletedAiInterview?: boolean;
  };
}

interface PublicAllOrganisationsResponse {
  data: {
    organisations: Organisation[];
  };
}

interface PublicCandidateListResponse {
  candidates: Array<{
    id: string | number;
    name: string;
    email: string;
    mobile?: string;
    phone?: string;
    countryCode?: string;
    profilePictureURL?: string;
    location?: string;
    matchPercentage?: number;
    latestExperience?: {
      position: string;
      company: string;
    };
    salaryExpectation?: number | { min: number; max: number, currency: string };
    resumeDetails?: any; // Use proper ResumeDetails type
    interviews?: any[]; // Use proper Interview type
    referralCode: string;
    yearsOfExperience?: number;
    noticePeriod?: number | string;
    notes?: any[];
    avatarUrl?: string;
    skills?: string[];
    experience?: string;
    availability?: string;
  }>;
  found: number;
  requested: number;
  // Fallback for backward compatibility
  data?: Array<any>;
}

interface OutreachRequest {
  jobTitle: string;
  companyName: string;
  candidateIds: string[];
  requesterEmail: string;
}

interface OutreachResponse {
  success: boolean;
  message: string;
  data?: {
    outreachId: string;
    status: string;
  };
}

export const publicApi = {
  // Get public organization details
  getOrganisation: async (orgId: string): Promise<PublicOrganisationResponse['data']> => {
    const response = await fetch(`${API_BASE_URL}/public/organisations/${orgId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleAPIResponse<PublicOrganisationResponse['data']>(response);
  },

  // Get public jobs list
  listJobs: async (
    orgId: string,
    { page, limit, status }: { page?: number; limit?: number; status?: string } = {}
  ): Promise<PublicJobsResponse['data']> => {
    const publicJobsUrl = new URL(`${API_BASE_URL}/public/organisations/${orgId}/jobs`);

    if (page) publicJobsUrl.searchParams.set('page', page.toString());
    if (limit) publicJobsUrl.searchParams.set('limit', limit.toString());
    if (status) publicJobsUrl.searchParams.set('status', status);

    const response = await fetch(publicJobsUrl.toString(), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleAPIResponse<PublicJobsResponse['data']>(response);
  },

  // Get public job details
  getJob: async (orgId: string, jobId: string): Promise<PublicJobResponse['data']> => {
    const response = await fetch(`${API_BASE_URL}/public/organisations/${orgId}/jobs/${jobId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleAPIResponse<PublicJobResponse['data']>(response);
  },

  // Get public candidate details
  getCandidateByCode: async (candidateCode: string, jobId?: string): Promise<PublicCandidateResponse['data']> => {
    const url = jobId
      ? `${API_BASE_URL}/public/candidates/${candidateCode}?jobId=${encodeURIComponent(jobId)}`
      : `${API_BASE_URL}/public/candidates/${candidateCode}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleAPIResponse<PublicCandidateResponse['data']>(response);
  },

  /**
   * Get candidate by code with auth-aware handling.
   * Sends cookies (credentials: 'include') so backend can verify auth for protected orgs.
   * Returns PublicCandidateApiResponse for determineAuthState to handle all cases
   * (SUCCESS, AUTH_REQUIRED, ACCESS_DENIED, NOT_FOUND, etc.) without throwing.
   */
  getCandidateByCodeWithAuth: async (
    candidateCode: string,
    jobId?: string
  ): Promise<PublicCandidateApiResponse> => {
    const url = jobId
      ? `${API_BASE_URL}/public/candidates/${candidateCode}?jobId=${encodeURIComponent(jobId)}`
      : `${API_BASE_URL}/public/candidates/${candidateCode}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Send cookies for auth when org requires it
    });

    let body: { message?: string; data?: { candidate?: Candidate }; requiresAuth?: boolean; candidateCode?: string; statusCode?: number } = {};
    try {
      body = await response.json();
    } catch {
      return {
        type: 'SERVER_ERROR',
        message: 'Invalid server response',
        statusCode: response.status,
      };
    }

    if (response.ok) {
      return {
        type: 'SUCCESS',
        data: body.data?.candidate,
      };
    }

    switch (response.status) {
      case 401:
        return body.requiresAuth
          ? {
              type: 'AUTH_REQUIRED',
              candidateCode: body.candidateCode ?? candidateCode,
              message: body.message ?? 'Authentication required',
            }
          : {
              type: 'ACCESS_DENIED',
              message: body.message ?? 'Authentication required',
              reason: 'OTHER',
            };
      case 403:
        return {
          type: 'ACCESS_DENIED',
          message: body.message ?? "You don't have access to candidates from this organization",
          reason: 'WRONG_ORG',
        };
      case 404:
        return {
          type: 'NOT_FOUND',
          message: body.message ?? 'Candidate not found',
        };
      case 500:
      case 501:
      case 502:
      case 503:
      case 504:
        return {
          type: 'SERVER_ERROR',
          message: body.message ?? 'Server error occurred',
          statusCode: response.status,
        };
      default:
        return {
          type: 'ERROR',
          message: body.message ?? 'An error occurred',
          statusCode: response.status,
        };
    }
  },

  getAllOrganisations: async (): Promise<PublicAllOrganisationsResponse['data']> => {
    const response = await fetch(`${API_BASE_URL}/public/organisations`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleAPIResponse<PublicAllOrganisationsResponse['data']>(response);
  },

  /**
   * Fetch candidates by their referral codes (public endpoint)
   * @param referralCodes Array of referral codes to fetch
   * @param jobId Optional job ID to filter interviews
   * @returns Promise with candidate data
   */
  getCandidatesByRefs: async (referralCodes: string[], jobId?: string): Promise<PublicCandidateListResponse> => {
    try {
      // Construct query string with refs parameter
      const refsParam = referralCodes.join(',');
      const url = jobId
        ? `${API_BASE_URL}/public/candidates?refs=${encodeURIComponent(refsParam)}&jobId=${encodeURIComponent(jobId)}`
        : `${API_BASE_URL}/public/candidates?refs=${encodeURIComponent(refsParam)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Note: This is a public endpoint, so no auth headers needed
        },
      });

      return handleAPIResponse<PublicCandidateListResponse>(response);
    } catch (error) {
      console.error('Failed to fetch candidates by referral codes:', error);
      throw new Error('Failed to fetch candidates');
    }
  },

  /**
   * Get a single candidate by referral code (public endpoint)
   * @param referralCode Referral code of the candidate
   * @param jobId Optional job ID to filter interviews
   * @returns Promise with candidate data
   */
  getCandidateByRef: async (referralCode: string, jobId?: string): Promise<PublicCandidateResponse['data']> => {
    try {
      const url = jobId
        ? `${API_BASE_URL}/public/candidates/${referralCode}?jobId=${encodeURIComponent(jobId)}`
        : `${API_BASE_URL}/public/candidates/${referralCode}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return handleAPIResponse<PublicCandidateResponse['data']>(response);
    } catch (error) {
      console.error('Failed to fetch candidate by referral code:', error);
      throw new Error('Failed to fetch candidate');
    }
  },

  /**
   * Submit outreach request for selected candidates
   * @param request Outreach request data
   * @returns Promise with outreach response
   */
  submitOutreachRequest: async (request: OutreachRequest): Promise<OutreachResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/public/outreach/call-candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'An error occurred' }));
        throw new Error(errorData.message || 'Failed to submit outreach request');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to submit outreach request:', error);
      throw new Error('Failed to submit outreach request');
    }
  },
};
